import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ChevronRight, Calendar, MapPin, Trophy, Zap } from "lucide-react";
import Particles from "../components/Particles";
import { Section, SectionTitle, Reveal, StatusBadge, TeamLogo } from "../components/UIBits";
import { LOGO_URL, TOURNAMENT, HERO_PLAYERS_PHOTO } from "../lib/constants";
import { useT, formatDayLocalized } from "../lib/i18n";
import api from "../lib/api";

export default function HomePage() {
    const { t, lang } = useT();
    const [news, setNews] = useState([]);
    const [games, setGames] = useState([]);
    const [teams, setTeams] = useState([]);
    const [partners, setPartners] = useState([]);

    useEffect(() => {
        api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
        api.get("/games").then((r) => setGames(r.data)).catch(() => {});
        api.get("/teams?light=true").then((r) => setTeams(r.data)).catch(() => {});
        api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
    }, []);

    const teamMap = Object.fromEntries(teams.map((team) => [team.id, team]));
    const liveGames = games.filter((g) => g.status === "live").slice(0, 3);

    return (
        <div data-testid="home-page">
            <Helmet>
                <title>{`${TOURNAMENT.name} — ${t("tournament.edition")}`}</title>
                <meta name="description" content={t("tournament.tagline")} />
            </Helmet>

            <section className="relative overflow-hidden min-h-[100vh] flex items-center" data-testid="hero-section">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_PLAYERS_PHOTO})` }} aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/65 to-black pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent pointer-events-none lg:block hidden" />
                <Particles />
                <div className="absolute inset-0 diag-stripes pointer-events-none" />
                <div className="absolute inset-0 pointer-events-none">
                    <div className="ice-crack" style={{ top: "20%", left: "10%", width: "30%", transform: "rotate(-12deg)" }} />
                    <div className="ice-crack" style={{ top: "60%", left: "55%", width: "40%", transform: "rotate(8deg)" }} />
                    <div className="ice-crack" style={{ top: "85%", left: "5%", width: "25%", transform: "rotate(-4deg)" }} />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-10 items-center py-20">
                    <div className="lg:col-span-6">
                        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="h-px w-16 bg-[#4FE0D5]" />
                                <span className="label-mini">{t("tournament.edition")}</span>
                            </div>
                            <h1 className="hero-title mb-6">
                                Défi <span className="accent">Ultime</span>
                            </h1>
                            <p className="text-lg sm:text-xl text-white/80 max-w-2xl mb-10 leading-relaxed">
                                {t("home.hero_subtitle")}
                            </p>
                            <div className="flex flex-wrap gap-4 mb-10">
                                <Link to="/inscription" data-testid="cta-inscription" className="btn-primary">
                                    <Zap size={18} /> {t("home.cta_inscrire")}
                                </Link>
                                <Link to="/horaire" data-testid="cta-acceder-site" className="btn-outline">
                                    {t("home.cta_horaire")}
                                </Link>
                            </div>
                            <div className="grid grid-cols-3 gap-4 max-w-xl">
                                {[
                                    { icon: Calendar, label: t("home.stat_date"), value: t("tournament.date") },
                                    { icon: MapPin, label: t("home.stat_lieu"), value: TOURNAMENT.arena.split(" ").slice(0, 3).join(" ") },
                                    { icon: Trophy, label: t("home.stat_categories"), value: "M11 · M13 · M15 · M18 · M21" },
                                ].map((s, i) => (
                                    <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }} className="glass-card clip-corner p-4">
                                        <s.icon size={18} className="text-[#7FD93C] mb-2" />
                                        <div className="label-mini text-[#4FE0D5]">{s.label}</div>
                                        <div className="text-sm mt-1 font-semibold">{s.value}</div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                    <div className="lg:col-span-6 relative hidden lg:flex items-center justify-center">
                        <motion.div initial={{ opacity: 0, scale: 0.7, rotate: -6 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 1, delay: 0.2, type: "spring", stiffness: 60 }} className="relative">
                            <div className="absolute inset-0 rounded-full blur-3xl opacity-70" style={{ background: "radial-gradient(circle, rgba(127,217,60,0.35), rgba(79,224,213,0.2) 40%, transparent 70%)" }} />
                            <img src={LOGO_URL} alt={TOURNAMENT.name} className="relative w-full max-w-[560px] mx-auto glow-pulse" style={{ filter: "drop-shadow(0 0 40px rgba(127, 217, 60, 0.4))" }} />
                        </motion.div>
                    </div>
                </div>
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#4FE0D5] text-xs tracking-[0.4em] uppercase animate-pulse">
                    {t("home.scroll_discover")}
                </div>
            </section>

            {liveGames.length > 0 && (
                <Section>
                    <SectionTitle kicker={t("home.live_kicker")} title={t("home.live_title")} accent={t("home.live_accent")} />
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {liveGames.map((g) => (
                            <Link key={g.game_num} to={`/match/${g.game_num}`} className="glass-card p-5 border-l-4 border-[#7FD93C] hover:bg-white/5 transition-colors">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="label-mini">#{g.game_num} • {g.category}</span>
                                    <StatusBadge status={g.status} />
                                </div>
                                <div className="grid grid-cols-3 items-center gap-2">
                                    <div className="flex items-center gap-2"><TeamLogo team={teamMap[g.away_team_id]} size={32} /><span className="truncate">{teamMap[g.away_team_id]?.name || g.away_placeholder}</span></div>
                                    <div className="text-center font-display text-3xl">{g.away_score} <span className="text-white/30">—</span> {g.home_score}</div>
                                    <div className="flex items-center gap-2 justify-end"><span className="truncate text-right">{teamMap[g.home_team_id]?.name || g.home_placeholder}</span><TeamLogo team={teamMap[g.home_team_id]} size={32} /></div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </Section>
            )}

            {news.length > 0 && (
                <Section>
                    <div className="flex items-end justify-between mb-10">
                        <SectionTitle kicker={t("home.news_kicker")} title={t("home.news_title")} accent={t("home.news_accent")} />
                        <Link to="/nouvelles" className="hidden md:inline-flex font-display text-[#4FE0D5] hover:text-[#7FD93C] tracking-widest text-sm uppercase items-center gap-1">
                            {t("common.see_all")} <ChevronRight size={16} />
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {news.map((n, i) => (
                            <Reveal key={n.id} delay={i * 0.08}>
                                <Link to={`/nouvelles/${n.id}`} className="block glass-card overflow-hidden h-full hover:border-[#7FD93C] transition-colors">
                                    {n.image_base64 ? (
                                        <img src={n.image_base64} alt={n.title} className="w-full h-48 object-cover" style={{ objectPosition: `50% ${n.image_focal_y ?? 50}%` }} />
                                    ) : (
                                        <div className="w-full h-48 diag-stripes bg-[#121212] flex items-center justify-center">
                                            <img src={LOGO_URL} alt="" className="h-20 w-20 opacity-40" />
                                        </div>
                                    )}
                                    <div className="p-5">
                                        <div className="label-mini mb-2">{new Date(n.created_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA")}</div>
                                        <h3 className="font-display text-2xl uppercase tracking-wide mb-2">{n.title}</h3>
                                        <p className="text-sm text-white/60 line-clamp-3">{n.content}</p>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </Section>
            )}

            <Section>
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    <Reveal className="lg:col-span-7">
                        <div className="label-mini mb-3">{t("home.kicker_about")}</div>
                        <h2 className="font-display text-4xl sm:text-6xl uppercase leading-[0.9] mb-6">
                            {t("home.about_title_pre")} <span className="text-[#7FD93C]">{t("home.about_title_accent")}</span> {t("home.about_title_post")}
                        </h2>
                        <p className="text-white/70 text-lg leading-relaxed mb-4">
                            {t("home.about_body", { name: TOURNAMENT.name })}
                        </p>
                    </Reveal>
                    <Reveal delay={0.2} className="lg:col-span-5">
                        <div className="relative aspect-square overflow-hidden">
                            <img src={HERO_PLAYERS_PHOTO} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6">
                                <div className="font-display text-3xl text-[#7FD93C]">{t("home.about_badge_top")}</div>
                                <div className="text-sm text-white/80">{t("home.about_badge_bottom")}</div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </Section>

            {partners.length > 0 && (
                <Section>
                    <div className="text-center mb-10">
                        <SectionTitle kicker={t("home.partners_kicker")} title={t("home.partners_title")} />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
                        {partners.map((p) => (
                            <a
                                key={p.id}
                                href={p.link || "#"}
                                target={p.link ? "_blank" : undefined}
                                rel={p.link ? "noreferrer" : undefined}
                                className="group relative bg-white flex items-center justify-center aspect-[3/2] p-4 sm:p-6 transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_10px_40px_-10px_rgba(127,217,60,0.6)]"
                                data-testid={`partner-${p.id}`}
                            >
                                {p.logo_base64 ? (
                                    <img src={p.logo_base64} alt={p.name} className="max-h-20 sm:max-h-24 max-w-full object-contain" loading="lazy" />
                                ) : (
                                    <span className="font-display text-xl sm:text-2xl tracking-wider text-black text-center leading-tight">{p.name}</span>
                                )}
                                <span className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-[#7FD93C] to-[#4FE0D5] opacity-0 group-hover:opacity-100 transition-opacity" />
                            </a>
                        ))}
                    </div>
                </Section>
            )}
        </div>
    );
}
